
import { useRef, useEffect } from "react";
import { UserCircle, BotIcon } from "lucide-react";
import { ChatMessage } from "@/hooks/useChatSession";

interface ChatMessageListProps {
  messages: ChatMessage[];
  promptId: string | null;
}

const ChatMessageList = ({ messages, promptId }: ChatMessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  if (messages.length === 0 && !promptId) {
    return (
      <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <BotIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-medium mb-2">Select a template to begin</h3>
          <p className="text-muted-foreground">
            Choose a template from the sidebar to start a conversation with Product Copilot.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex ${
            message.role === "user" ? "justify-end" : "justify-start"
          }`}
        >
          <div
            className={`max-w-[85%] sm:max-w-[75%] rounded-lg px-4 py-2 ${
              message.role === "user"
                ? "bg-primary text-primary-foreground"
                : message.role === "system"
                ? "bg-muted/80 border border-border/50"
                : "bg-[#F8F9FA] dark:bg-gray-800 border border-border/50" // Light gray background for assistant
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {message.role === "user" ? (
                <UserCircle className="h-4 w-4" />
              ) : (
                <BotIcon className="h-4 w-4" />
              )}
              <span className="text-xs font-medium">
                {message.role === "user" ? "You" : "Product Copilot"}
              </span>
            </div>
            <div className="whitespace-pre-wrap">{message.content}</div>
            <div className="text-xs opacity-70 text-right mt-1">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessageList;
